import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, IconButton, Divider, Grid, MenuItem } from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '600px',
    
    color: '#f8fafc'
  },
  textField: {
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-root': {
      color: '#f8fafc' },
    '& .Mui-root': {
      color: theme.palette.text.secondary },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#334155' }
  },
  sectionTitle: {
    color: '#38bdf8',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  listItem: {
    background: theme.palette.type === "dark" ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.4)",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    border: '1px solid #334155'
  }
}));

export default function AyahEditDialog({ open, ayah, onClose, onSave }) {
  const classes = useStyles();
  
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (ayah) {
      setFormData(JSON.parse(JSON.stringify(ayah))); // deep copy
    }
  }, [ayah]);

  if (!formData) return null;

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    const newArray = [...formData[arrayName]];
    newArray[index][field] = value;
    handleChange(arrayName, newArray);
  };

  const handleAddTafsir = () => {
    const newArray = [...(formData.tafsirs || [])];
    newArray.push({ source: '', textEn: '', textBn: '' });
    handleChange('tafsirs', newArray);
  };

  const handleAddArrayItem = (arrayName) => {
    const newArray = [...(formData[arrayName] || [])];
    if (arrayName === 'translations') {
      newArray.push({ language: 'en', source: '', text: '' });
    } else if (arrayName === 'tafsirs') {
      newArray.push({ source: '', textEn: '', textBn: '' });
    } else if (arrayName === 'audio') {
      newArray.push({ source: '', url: '' });
    }
    handleChange(arrayName, newArray);
  };

  const handleRemoveArrayItem = (arrayName, index) => {
    const newArray = [...formData[arrayName]];
    newArray.splice(index, 1);
    handleChange(arrayName, newArray);
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="md" fullWidth>
      <DialogTitle>Edit Ayah {formData.numberInSurah}</DialogTitle>
      <DialogContent dividers>
        <Typography className={classes.sectionTitle}>Core Arabic & Audio</Typography>
        <TextField
          label="Arabic Text"
          fullWidth
          multiline
          variant="outlined"
          className={classes.textField}
          value={formData.text}
          onChange={(e) => handleChange('text', e.target.value)}
          inputProps={{ style: { fontSize: '1.5rem', textAlign: 'right' } }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.sectionTitle}>Audio Sources</Typography>
          <Button startIcon={<AddCircleOutlineIcon />} color="primary" onClick={() => handleAddArrayItem('audio')}>
            Add Audio
          </Button>
        </Box>
        {(formData.audio || []).map((a, idx) => (
          <Box key={idx} className={classes.listItem}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={5}>
                <TextField
                  label="Source Name (e.g. Mishary Alafasy)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={a.source || ''}
                  onChange={(e) => handleArrayChange('audio', idx, 'source', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Audio URL"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={a.url || ''}
                  onChange={(e) => handleArrayChange('audio', idx, 'url', e.target.value)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton style={{ color: '#ef4444' }} onClick={() => handleRemoveArrayItem('audio', idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        
        <Typography className={classes.sectionTitle}>Transliteration</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="English Transliteration"
              fullWidth
              multiline
              variant="outlined"
              className={classes.textField}
              value={formData.transliterationEn || ''}
              onChange={(e) => handleChange('transliterationEn', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Bangla Transliteration"
              fullWidth
              multiline
              variant="outlined"
              className={classes.textField}
              value={formData.transliterationBn || ''}
              onChange={(e) => handleChange('transliterationBn', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider style={{ backgroundColor: '#334155', margin: '20px 0' }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.sectionTitle}>Translations</Typography>
          <Button startIcon={<AddCircleOutlineIcon />} color="primary" onClick={() => handleAddArrayItem('translations')}>
            Add Translation
          </Button>
        </Box>
        {(formData.translations || []).map((t, idx) => (
          <Box key={idx} className={classes.listItem}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  select
                  label="Language"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.language || 'en'}
                  onChange={(e) => handleArrayChange('translations', idx, 'language', e.target.value)}
                >
                  <MenuItem value="en">English (en)</MenuItem>
                  <MenuItem value="bn">Bangla (bn)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  label="Source Name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.source}
                  onChange={(e) => handleArrayChange('translations', idx, 'source', e.target.value)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton style={{ color: '#ef4444' }} onClick={() => handleRemoveArrayItem('translations', idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Translation Text"
                  fullWidth
                  multiline
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.text}
                  onChange={(e) => handleArrayChange('translations', idx, 'text', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

        <Divider style={{ backgroundColor: '#334155', margin: '20px 0' }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.sectionTitle}>Explanations / Tafsir</Typography>
          <Button startIcon={<AddCircleOutlineIcon />} color="primary" onClick={handleAddTafsir}>
            Add Tafsir
          </Button>
        </Box>
        {(formData.tafsirs || []).map((t, idx) => (
          <Box key={idx} className={classes.listItem}>
            <Grid container spacing={2}>
              <Grid item xs={11}>
                <TextField
                  label="Source Name (e.g. Ibn Kathir)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.source}
                  onChange={(e) => handleArrayChange('tafsirs', idx, 'source', e.target.value)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton style={{ color: '#ef4444' }} onClick={() => handleRemoveArrayItem('tafsirs', idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="English Tafsir"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.textEn || (t.language === 'en' ? t.text : '')}
                  onChange={(e) => handleArrayChange('tafsirs', idx, 'textEn', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Bangla Tafsir"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  value={t.textBn || (t.language === 'bn' ? t.text : '')}
                  onChange={(e) => handleArrayChange('tafsirs', idx, 'textBn', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

      </DialogContent>
      <DialogActions >
        <Button onClick={onClose} color="textSecondary">
          Cancel
        </Button>
        <Button onClick={() => onSave(formData)} color="primary" variant="contained" disableElevation>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
